import { productsConfig, ProductsId, UserType, extendedProductRules } from '@webc/meeseeks-ui-sdk';
import { StoreProductField } from 'app/models/types/store/product';

const defaultKey = 'Any-Any';

function isProductIds(arg: ProductsId[] | undefined): arg is ProductsId[] {
  return arg !== undefined;
}

const getPassengerRules = (extended: boolean) => {
  if (extended) {
    return productsConfig.countryPassengerRulesExtended;
  }

  return productsConfig.countryPassengerRules;
};

/**
 * products rules differ from country to country and this function make sure to apply correct rules
 * it does so by retrieving the overall product config and extracting the pax types that match countries
 * of current search.
 * Rules are always referenced as "DE-DE" where "Any-Any" is the default rule for country which do NOT have specific rules
 *
 * Further more this function applies any custom rule which only exists for search.
 * This rules are described in extendedProductRules
 *
 */
export const getCountryPassengersByCountryCodes = (
  allProducts: StoreProductField['types'],
  departureCountryCode: string | undefined,
  arrivalCountryCode: string | undefined,
  departureSubdivisionCode: string | null,
  arrivalSubdivisionCode: string | null,
  userType: UserType,
  options = { extended: false },
): Partial<StoreProductField['types']> => {
  const countryId = `${departureCountryCode}-${arrivalCountryCode}`;
  const subdivisionId = `${departureSubdivisionCode?.replace(
    '-',
    '_',
  )}-${arrivalSubdivisionCode?.replace('-', '_')}`;
  const passengerRules = getPassengerRules(options.extended);
  const countryRule = passengerRules[countryId];
  const subdivisionRule = passengerRules[subdivisionId];
  const extendedRule = extendedProductRules[countryId] && extendedProductRules[countryId][userType];
  const countryRuleProducts = isProductIds(countryRule) ? countryRule : passengerRules[defaultKey];
  const subdivisionRuleProducts = isProductIds(subdivisionRule) ? subdivisionRule : [];

  if (!countryRuleProducts && !subdivisionRuleProducts.length) return {};
  const rule = [...subdivisionRuleProducts, ...(countryRuleProducts || [])];

  const maxTotalAmount = extendedRule?.maxTotalAmount;
  let currentProductAmount = 0;

  return rule.reduce(
    (acc, c) => {
      if (!extendedRule) {
        acc[c] = { ...allProducts[c as ProductsId] };
      } else if (extendedRule && extendedRule[c]) {
        acc[c] = { ...allProducts[c as ProductsId] };
        acc[c].maxAmount = extendedRule[c]?.maxAmount || 0;
      }
      if (acc[c] && acc[c].selectedAmount >= acc[c].maxAmount) {
        acc[c].selectedAmount = acc[c].maxAmount;
      }

      if (maxTotalAmount && productsConfig.productTypes[c].type !== 'device') {
        const availableAmount = maxTotalAmount - currentProductAmount;
        acc[c].selectedAmount =
          acc[c].selectedAmount > availableAmount ? availableAmount : acc[c].selectedAmount;
        currentProductAmount += acc[c]?.selectedAmount ? acc[c].selectedAmount : 0;
      }
      return acc;
    },
    {} as StoreProductField['types'],
  );
};
